import React from "react"
import { Badge } from "@love-irish/ui-components"
import { twMerge } from "tailwind-merge"

import { KindBadge } from "./kind_badge"
import type { CodexSectionView } from "./senses"

export interface CodexTag {
  key: string
  label: string
}

/**
 * Derives the dynamic tag list from a codex record. Which tags exist depends on the record: dialect
 * (if set), "audio" when there's a recording, and part-of-speech / register / domain / genre / entity
 * pulled from its senses (deduped). The `kind` is rendered separately via KindBadge in CodexTagList.
 */
export function buildCodexTags(item: any): CodexTag[] {
  const tags: CodexTag[] = []
  const push = (key: string, label?: string | null) => {
    if (label && !tags.some((tag) => tag.key === key)) tags.push({ key, label })
  }

  if (item?.dialect) push(`dialect:${item.dialect}`, item.dialect)
  if (item?.audioUrl || item?.audio_url) push("audio", "audio")

  for (const section of (item?.sections ?? []) as CodexSectionView[]) {
    push(
      `pos:${section.partOfSpeech}`,
      section.partOfSpeechShort || section.partOfSpeech
    )
    for (const sense of section.senses ?? []) {
      push(`register:${sense.register}`, sense.register)
      push(`domain:${sense.domain}`, sense.domainLabel)
      push(`genre:${sense.genre}`, sense.genreLabel)
      push(`entity:${sense.entityType}`, sense.entityLabel)
    }
  }

  return tags
}

/** The record's tags as a chip row: KindBadge for the kind, plain outline badges for the rest. */
export const CodexTagList: React.FC<{ item: any; className?: string }> = ({
  item,
  className,
}) => {
  const tags = buildCodexTags(item)
  if (!item?.kind && tags.length === 0) return null

  return (
    <div
      className={twMerge("flex flex-wrap items-center gap-1.5", className)}
      data-testid="codex-tags"
    >
      {item?.kind && <KindBadge kind={item.kind} />}
      {tags.map((tag) => (
        <Badge
          key={tag.key}
          variant="outline"
          className="text-xs font-normal text-muted-foreground"
        >
          {tag.label}
        </Badge>
      ))}
    </div>
  )
}

export default CodexTagList
